// File: src/utils/ProtectedRoute.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
} from "react";

import {
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import { useAuth } from "@/context/AuthProvider";

/* ──────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────── */

function toArray(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeList(value) {
  return Array.from(
    new Set(
      toArray(value)
        .map(normalizeValue)
        .filter(Boolean)
    )
  );
}

function buildReturnTo(location) {
  const path =
    `${location.pathname}` +
    `${location.search || ""}` +
    `${location.hash || ""}`;

  return encodeURIComponent(
    path || "/"
  );
}

function buildRedirectUrl(
  base,
  returnTo
) {
  const target =
    String(base || "").trim() ||
    "/";

  return target.includes("?")
    ? `${target}&returnTo=${returnTo}`
    : `${target}?returnTo=${returnTo}`;
}

/* ──────────────────────────────────────────────────────────────
 * Public route helpers
 *
 * These remain for compatibility.
 *
 * Most truly public routes are already mounted outside ProtectedRoute
 * in App.jsx, but keeping this logic prevents older nested usages from
 * breaking during the migration.
 * ────────────────────────────────────────────────────────────── */

function isPublicPath({
  pathname,
  search,
  publicPaths,
  publicQueryBypassPaths,
  publicQueryKeys,
}) {
  const path =
    pathname || "/";

  const directPublic =
    publicPaths.some(
      (base) =>
        path === base ||
        path.startsWith(
          `${base}/`
        )
    );

  if (directPublic) {
    return true;
  }

  const tokenizedPublicPath =
    publicQueryBypassPaths.some(
      (base) =>
        path === base ||
        path.startsWith(
          `${base}/`
        )
    );

  if (!tokenizedPublicPath) {
    return false;
  }

  /*
   * Existing invoice payment route:
   *
   * /invoice/pay/:token
   */
  if (
    path.startsWith(
      "/invoice/pay/"
    )
  ) {
    return true;
  }

  if (!search) {
    return false;
  }

  const params =
    new URLSearchParams(
      search
    );

  return publicQueryKeys.some(
    (key) =>
      params.has(key)
  );
}

/* ──────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────────── */

export default function ProtectedRoute({
  /*
   * Transitional role gate.
   *
   * Existing App.jsx routes can continue using:
   *
   *   roles={["admin", "manager"]}
   */
  roles = [],

  /*
   * Forward-compatible explicit permission gates.
   *
   * Examples:
   *
   *   permission="invoice.approve"
   *
   * or:
   *
   *   permissions={[
   *     "invoice.approve",
   *     "invoice.pay"
   *   ]}
   *
   * Default behavior is ANY permission.
   */
  permission = null,
  permissions = [],
  requireAllPermissions = false,

  /*
   * Additional route-specific condition.
   */
  allowIf = null,

  /*
   * Platform administration UI.
   *
   * Derived only from server-returned /auth/me user state.
   */
  platformOnly = false,

  redirectUnauthed = "/login",
  redirectForbidden = "/forbidden",

  showSpinner = true,

  /*
   * Compatibility public-route support.
   */
  publicPaths = [
    "/reset-password",
    "/invite",
  ],

  publicQueryBypassPaths = [
    "/invoice/pay",
  ],

  publicQueryKeys = [
    "t",
    "token",
  ],

  children,
}) {
  const auth =
    useAuth();

  const location =
    useLocation();

  /*
   * SECURITY AUTHORITY:
   *
   * These values originate from AuthProvider after /auth/me.
   *
   * ProtectedRoute deliberately does NOT read:
   *
   * - localStorage
   * - sessionStorage
   * - JWT payloads
   * - cookies directly
   */
  const user =
    auth?.user || null;

  const ready =
    Boolean(
      auth?.ready
    );

  const authenticated =
    Boolean(
      auth?.authenticated &&
      user?.id
    );

  const rolesEffective =
    useMemo(() => {
      if (!authenticated) {
        return [];
      }

      return Array.from(
        new Set([
          ...(user?.role
            ? [
                normalizeValue(
                  user.role
                ),
              ]
            : []),

          ...normalizeList(
            user?.roles
          ),

          ...normalizeList(
            auth?.roles
          ),
        ])
      ).filter(Boolean);
    }, [
      authenticated,
      user,
      auth?.roles,
    ]);

  const permissionsEffective =
    useMemo(() => {
      if (!authenticated) {
        return [];
      }

      return Array.from(
        new Set([
          ...normalizeList(
            user?.permissions
          ),

          ...normalizeList(
            auth?.permissions
          ),
        ])
      );
    }, [
      authenticated,
      user,
      auth?.permissions,
    ]);

  const rolesSet =
    useMemo(
      () =>
        new Set(
          rolesEffective
        ),
      [rolesEffective]
    );

  const permissionsSet =
    useMemo(
      () =>
        new Set(
          permissionsEffective
        ),
      [permissionsEffective]
    );

  const firedPlatformDeniedRef =
    useRef(false);

  useEffect(() => {
    const path =
      String(
        location.pathname ||
        ""
      );

    if (
      !path.startsWith(
        "/admin"
      )
    ) {
      firedPlatformDeniedRef.current =
        false;
    }
  }, [
    location.pathname,
  ]);

  /* ────────────────────────────────────────────────────────────
   * Public-route compatibility
   * ──────────────────────────────────────────────────────────── */

  const isPublicRoute =
    useMemo(
      () =>
        isPublicPath({
          pathname:
            location.pathname,

          search:
            location.search,

          publicPaths,

          publicQueryBypassPaths,

          publicQueryKeys,
        }),
      [
        location.pathname,
        location.search,
        publicPaths,
        publicQueryBypassPaths,
        publicQueryKeys,
      ]
    );

  if (isPublicRoute) {
    return children
      ? <>{children}</>
      : <Outlet />;
  }

  /* ────────────────────────────────────────────────────────────
   * Session bootstrap
   *
   * IMPORTANT:
   *
   * We wait for /auth/me.
   *
   * There is no:
   *
   *   "if localStorage token exists..."
   *
   * logic anymore.
   * ──────────────────────────────────────────────────────────── */

  if (!ready) {
    if (!showSpinner) {
      return null;
    }

    return (
      <div className="min-h-[40vh] grid place-items-center text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="animate-spin h-4 w-4 inline-block rounded-full border-2 border-current border-r-transparent" />

          Checking permissions…
        </div>
      </div>
    );
  }

  /* ────────────────────────────────────────────────────────────
   * Authentication gate
   * ──────────────────────────────────────────────────────────── */

  if (!authenticated) {
    const returnTo =
      buildReturnTo(
        location
      );

    const destination =
      buildRedirectUrl(
        redirectUnauthed,
        returnTo
      );

    return (
      <Navigate
        to={destination}
        replace
        state={{
          from:
            location,

          reason:
            "unauthenticated",

          returnTo,
        }}
      />
    );
  }

  /* ────────────────────────────────────────────────────────────
   * Server-derived authorization helpers
   * ──────────────────────────────────────────────────────────── */

  const hasRole =
    (wantedRole) => {
      const wanted =
        normalizeList(
          wantedRole
        );

      if (
        wanted.length === 0
      ) {
        return false;
      }

      return wanted.some(
        (candidate) =>
          rolesSet.has(
            candidate
          )
      );
    };

  const hasAnyRole =
    (wantedRoles) => {
      const wanted =
        normalizeList(
          wantedRoles
        );

      if (
        wanted.length === 0
      ) {
        return false;
      }

      return wanted.some(
        (candidate) =>
          rolesSet.has(
            candidate
          )
      );
    };

  const hasPermission =
    (wantedPermission) => {
      const wanted =
        normalizeList(
          wantedPermission
        );

      if (
        wanted.length === 0
      ) {
        return false;
      }

      return wanted.some(
        (candidate) =>
          permissionsSet.has(
            candidate
          )
      );
    };

  const hasAnyPermission =
    (wantedPermissions) => {
      const wanted =
        normalizeList(
          wantedPermissions
        );

      if (
        wanted.length === 0
      ) {
        return false;
      }

      return wanted.some(
        (candidate) =>
          permissionsSet.has(
            candidate
          )
      );
    };

  const hasAllPermissions =
    (wantedPermissions) => {
      const wanted =
        normalizeList(
          wantedPermissions
        );

      if (
        wanted.length === 0
      ) {
        return false;
      }

      return wanted.every(
        (candidate) =>
          permissionsSet.has(
            candidate
          )
      );
    };

  const inCostCentre =
    (ccId) => {
      /*
       * Prefer AuthProvider's hardened helper.
       */
      if (
        typeof auth?.inCostCentre ===
        "function"
      ) {
        try {
          return Boolean(
            auth.inCostCentre(
              ccId
            )
          );
        } catch {
          return false;
        }
      }

      /*
       * Fail closed if the provider does not expose a scope helper.
       */
      return false;
    };

  /* ────────────────────────────────────────────────────────────
   * Platform-only gate
   *
   * There is NO localStorage/JWT fallback.
   * ──────────────────────────────────────────────────────────── */

  if (platformOnly) {
    const isPlatformAdmin =
      user?.is_platform_admin ===
      true;

    if (!isPlatformAdmin) {
      if (
        !firedPlatformDeniedRef.current
      ) {
        firedPlatformDeniedRef.current =
          true;

        /*
         * Preserve existing UI compatibility events.
         *
         * These are UX signals only.
         * They do not grant access.
         */
        try {
          window.dispatchEvent(
            new CustomEvent(
              "auth:platform-admin-denied"
            )
          );

          window.dispatchEvent(
            new CustomEvent(
              "auth:go-dashboard"
            )
          );
        } catch {
          // noop
        }
      }

      const returnTo =
        buildReturnTo(
          location
        );

      /*
       * Preserve the current deterministic platform-admin behavior:
       * non-platform users attempting /admin return to dashboard.
       */
      return (
        <Navigate
          to="/dashboard"
          replace
          state={{
            from:
              location,

            reason:
              "platform_forbidden",

            returnTo,
          }}
        />
      );
    }
  }

  /* ────────────────────────────────────────────────────────────
   * Transitional role gate
   *
   * NO super/admin bypass.
   *
   * Every role that should be allowed must be explicitly listed.
   * ──────────────────────────────────────────────────────────── */

  const allowedRoles =
    normalizeList(
      roles
    );

  if (
    allowedRoles.length > 0 &&
    !hasAnyRole(
      allowedRoles
    )
  ) {
    const returnTo =
      buildReturnTo(
        location
      );

    const destination =
      buildRedirectUrl(
        redirectForbidden,
        returnTo
      );

    return (
      <Navigate
        to={destination}
        replace
        state={{
          from:
            location,

          reason:
            "forbidden",

          returnTo,
        }}
      />
    );
  }

  /* ────────────────────────────────────────────────────────────
   * Explicit permission gate
   *
   * Supports both:
   *
   *   permission="invoice.approve"
   *
   * and:
   *
   *   permissions={[...]}
   * ──────────────────────────────────────────────────────────── */

  const wantedPermissions =
    normalizeList([
      ...toArray(
        permission
      ),

      ...toArray(
        permissions
      ),
    ]);

  if (
    wantedPermissions.length > 0
  ) {
    const permissionAllowed =
      requireAllPermissions
        ? hasAllPermissions(
            wantedPermissions
          )
        : hasAnyPermission(
            wantedPermissions
          );

    if (!permissionAllowed) {
      const returnTo =
        buildReturnTo(
          location
        );

      const destination =
        buildRedirectUrl(
          redirectForbidden,
          returnTo
        );

      return (
        <Navigate
          to={destination}
          replace
          state={{
            from:
              location,

            reason:
              "permission_forbidden",

            returnTo,
          }}
        />
      );
    }
  }

  /* ────────────────────────────────────────────────────────────
   * Custom route rule
   *
   * allowIf receives ONLY server-derived authority state.
   * ──────────────────────────────────────────────────────────── */

  if (
    typeof allowIf ===
    "function"
  ) {
    let allowed =
      false;

    try {
      allowed =
        Boolean(
          allowIf({
            user,

            authenticated,

            role:
              user?.role ||
              rolesEffective[0] ||
              null,

            roles:
              rolesEffective,

            permissions:
              permissionsEffective,

            isPlatformAdmin:
              user?.is_platform_admin ===
              true,

            hasRole,
            hasAnyRole,

            hasPermission,
            hasAnyPermission,
            hasAllPermissions,

            inCostCentre,
          })
        );
    } catch (err) {
      /*
       * Custom authorization rules fail closed.
       */
      if (
        import.meta.env.DEV
      ) {
        console.error(
          "[ProtectedRoute] allowIf() failed:",
          err
        );
      }

      allowed =
        false;
    }

    if (!allowed) {
      const returnTo =
        buildReturnTo(
          location
        );

      const destination =
        buildRedirectUrl(
          redirectForbidden,
          returnTo
        );

      return (
        <Navigate
          to={destination}
          replace
          state={{
            from:
              location,

            reason:
              "forbidden",

            returnTo,
          }}
        />
      );
    }
  }

  /* ────────────────────────────────────────────────────────────
   * Authorized
   * ──────────────────────────────────────────────────────────── */

  return children
    ? <>{children}</>
    : <Outlet />;
}